import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  FaArrowLeft,
  FaHashtag,
  FaFileContract,
  FaNetworkWired,
  FaBuilding,
  FaExchangeAlt,
  FaCheckDouble,
  FaSpinner,
} from "react-icons/fa";
import { useMutation } from "@apollo/client";
import toast from "react-hot-toast";
import payments from "../../gql/payments";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import AppButton from "../../shared/AppButton";

function PaymentDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  // --- State & Initialization ---
  const [loading, setLoading] = useState(true);
  const [rawPaymentData, setRawPaymentData] = useState(null);

  // --- 1. Load Data from Router State ---
  useEffect(() => {
    const stateData = location.state?.paymentData;
    if (!stateData) {
      navigate("/payments", { replace: true });
      return;
    }
    setRawPaymentData(stateData);
    setLoading(false);
  }, [location.state, navigate]);

  // --- 2. Advanced Parsing Logic (Extract All Fields) ---
  const parsedData = useMemo(() => {
    if (!rawPaymentData?.logs) return null;

    const result = {
      outerMessageHeader: null,
      innerMessageHeader: null,
      task: null,
      nestedBundle: null,
      reconciliation: null,
      providerOrg: null,
      insurerOrg: null,
      lineItems: [],
    };

    try {
      // Find the Poll Response Log
      const pollResponseLog = rawPaymentData?.logs?.find(
        (log) => log.type === "pollResponse",
      );

      if (pollResponseLog?.log) {
        const parsedLog = JSON.parse(pollResponseLog.log);
        // 1. Extract Outer Resources (MessageHeader & Task)
        if (parsedLog.entry) {
          result.outerMessageHeader = parsedLog.entry.find(
            (e) => e.resource?.resourceType === "MessageHeader",
          )?.resource;
          result.task = parsedLog.entry.find(
            (e) => e.resource?.resourceType === "Task",
          )?.resource;

          // 2. Find Nested Bundle (The Payload)
          const nestedEntry = parsedLog.entry.find(
            (e) => e.resource?.resourceType === "Bundle",
          );
          if (nestedEntry?.resource) {
            result.nestedBundle = nestedEntry.resource;
            const innerEntries = result.nestedBundle.entry || [];

            // 3. Extract Inner MessageHeader
            result.innerMessageHeader = innerEntries.find(
              (e) => e.resource?.resourceType === "MessageHeader",
            )?.resource;

            // 4. Extract PaymentReconciliation
            result.reconciliation = innerEntries.find(
              (e) => e.resource?.resourceType === "PaymentReconciliation",
            )?.resource;

            // 5. Try to extract Organizations from entries (if they exist)
            result.providerOrg = innerEntries.find(
              (e) =>
                e.resource?.resourceType === "Organization" &&
                e.resource?.type?.[0]?.coding?.[0]?.code === "prov",
            )?.resource;

            result.insurerOrg = innerEntries.find(
              (e) =>
                e.resource?.resourceType === "Organization" &&
                e.resource?.type?.[0]?.coding?.[0]?.code === "ins",
            )?.resource;

            // 6. If organizations not found in entries, extract from MessageHeader identifiers
            if (!result.insurerOrg && result.innerMessageHeader?.sender) {
              result.insurerOrg = {
                name: rawPaymentData.payer_name_en || "Unknown Insurer",
                identifier: result.innerMessageHeader.sender.identifier
                  ? [result.innerMessageHeader.sender.identifier]
                  : [],
                type: [
                  {
                    coding: [
                      {
                        code: "ins",
                        display: "Insurance Company",
                      },
                    ],
                  },
                ],
              };
            }

            if (
              !result.providerOrg &&
              result.innerMessageHeader?.destination?.[0]?.receiver
            ) {
              result.providerOrg = {
                name: rawPaymentData.providers_name_en || "Unknown Provider",
                identifier: result.innerMessageHeader.destination[0].receiver
                  .identifier
                  ? [
                      result.innerMessageHeader.destination[0].receiver
                        .identifier,
                    ]
                  : [],
                type: [
                  {
                    coding: [
                      {
                        code: "prov",
                        display: "Healthcare Provider",
                      },
                    ],
                  },
                ],
              };
            }

            // 7. Process Line Items & Extensions
            if (result.reconciliation?.detail) {
              result.lineItems = result.reconciliation.detail.map((item) => {
                // Extract Breakdown from Extensions (with safe navigation)
                const extPayment = item.extension?.find((x) =>
                  x.url?.includes("extension-component-payment"),
                )?.valueMoney;
                const extEarlyFee = item.extension?.find((x) =>
                  x.url?.includes("extension-component-early-fee"),
                )?.valueMoney;
                const extNphiesFee = item.extension?.find((x) =>
                  x.url?.includes("extension-component-nphies-fee"),
                )?.valueMoney;

                return {
                  ...item,
                  breakdown: {
                    payment: extPayment || { value: 0, currency: "SAR" },
                    earlyFee: extEarlyFee || { value: 0, currency: "SAR" },
                    nphiesFee: extNphiesFee || { value: 0, currency: "SAR" },
                  },
                };
              });
            }
          }
        }
      }
    } catch (err) {
      console.error("Parsing Error:", err);
      toast.error("Error parsing payment data");
    }

    return result;
  }, [rawPaymentData]);

  // --- Helpers ---
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (val, curr) => {
    if (val === undefined || val === null) return "-";
    return `${parseFloat(val).toLocaleString("en-US", { minimumFractionDigits: 2 })} ${curr || "SAR"}`;
  };

  const [confirmReq, { data, loading: confirmLoading, error: confirmError }] =
    useMutation(payments.PAYMENT_NOTICE, {
      update(proxy, result) {
        onSuccessfulRequestResponse(result.data);
      },
      onError(error) {
        onResponseError(error.graphQLErrors[0]);
      },
    });
  const [errorData, setErrorData] = useState([]);

  const onSuccessfulRequestResponse = (data) => {
    if (data.paymentNoticeRequest?.nphies_err) {
      setErrorData(data.paymentNoticeRequest.nphies_err);
    } else {
      toast.success(data?.paymentNoticeRequest?.message);
      navigate(-1);
    }
  };

  const onResponseError = (error) => {
    toast.error(`${error?.message}`);
  };

  const confirmPayment = () => {
    confirmReq({ variables: { id: location?.state?.paymentData?.id } });
  };

  if (loading || !rawPaymentData)
    return (
      <div className="p-10 text-center text-gray-500">
        {t("Loading Dashboard...")}
      </div>
    );

  const {
    reconciliation,
    task,
    outerMessageHeader,
    providerOrg,
    insurerOrg,
    lineItems,
    nestedBundle,
  } = parsedData || {};

  return (
    <div
      className="min-h-screen font-sans text-slate-900 pb-12"
      dir={i18n.dir()}
    >
      {/* === HEADER === */}
      <header className="mt-1">
        <div className="w-full">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(-1)}
                className={`p-1 bg-[#0070C2] rounded-full text-white transition-colors ${i18n.dir() === "rtl" ? "rotate-180" : ""}`}
              >
                <FaArrowLeft size={14} />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-slate-900">
                    {t("Payment Reconciliation")}
                  </h1>
                  <span
                    className={`px-3 py-1 rounded text-xs font-bold uppercase tracking-wide ${
                      rawPaymentData?.confirmation_status?.toLowerCase() ===
                      "cleared"
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {rawPaymentData.confirmation_status?.toUpperCase() ||
                      t("Pending")}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                  <span>
                    {formatDateTime(rawPaymentData.payment_note_date)}
                  </span>
                </div>
              </div>
            </div>
            <div>
              {rawPaymentData?.confirmation_status?.toLowerCase() !=
                "cleared" && (
                <AppButton
                  disabled={confirmLoading}
                  isLoading={confirmLoading}
                  autoFocus
                  onClick={confirmPayment}
                >
                  {t("Confirm Payment")}
                </AppButton>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className=" grid grid-cols-1 xl:grid-cols-[1fr_350px] gap-2 py-2">
        {/* === LEFT MAIN CONTENT === */}
        <main className="space-y-2">
          {/* 1. FINANCIAL SUMMARY CARD */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="p-3 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 flex flex-wrap justify-between items-center gap-4">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  {t("Total Reconciled Amount")}
                </span>
                <div className="text-2xl font-extrabold text-emerald-600 mt-1">
                  {formatCurrency(
                    rawPaymentData.payment_amount,
                    rawPaymentData.payment_currency,
                  )}
                </div>
              </div>
              <div className="flex gap-8 text-sm">
                <div>
                  <span className="block text-xs text-slate-400 font-semibold uppercase mb-1">
                    {t("Billing Period")}
                  </span>
                  <span className="font-medium text-slate-800">
                    {formatDate(rawPaymentData.payment_start_date)} —{" "}
                    {formatDate(rawPaymentData.payment_end_date)}
                  </span>
                </div>
                <div>
                  <span className="block text-xs text-slate-400 font-semibold uppercase mb-1">
                    {t("Payment Date")}
                  </span>
                  <span className="font-medium text-slate-800">
                    {formatDate(rawPaymentData.payment_note_date)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 2. PROCESS CONTEXT (Task & Header Info) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {/* Task Card */}
            <div className="bg-white rounded-lg border border-slate-200  overflow-hidden">
              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-300 bg-gray-100 p-2">
                <FaFileContract className="text-[#0070C2]" />
                <h3 className="font-bold text-sm uppercase text-slate-700">
                  {t("Request Task Context")}
                </h3>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm p-2">
                <div>
                  <dt className="text-xs text-slate-500 uppercase">
                    {t("Task Status")}
                  </dt>
                  <dd className="font-medium text-slate-900 flex items-center gap-2">
                    {task?.status === "completed" && (
                      <FaCheckDouble className="text-green-500" />
                    )}
                    <span className="capitalize">{task?.status || "-"}</span>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500 uppercase">
                    {t("Intent / Priority")}
                  </dt>
                  <dd className="font-medium text-slate-900 capitalize">
                    {task?.intent || "-"} /{" "}
                    <span className="text-orange-600">
                      {task?.priority || "-"}
                    </span>
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-xs text-slate-500 uppercase">
                    {t("Requester Ref")}
                  </dt>
                  <dd className="font-mono text-xs text-slate-600 bg-slate-50 p-1 rounded mt-1 truncate">
                    {task?.requester?.reference || "-"}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Transport Card */}
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-300 p-2 bg-gray-100">
                <FaNetworkWired className="text-[#0070C2]" />
                <h3 className="font-bold text-sm uppercase text-slate-700">
                  {t("Transport Layer")}
                </h3>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm p-2">
                <div>
                  <dt className="text-xs text-slate-500 uppercase">
                    {t("Event Code")}
                  </dt>
                  <dd className="font-medium text-slate-900 font-mono text-xs">
                    {outerMessageHeader?.eventCoding?.code || "-"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500 uppercase">
                    {t("Response Code")}
                  </dt>
                  <dd className="font-medium text-slate-900">
                    <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs uppercase font-bold">
                      {outerMessageHeader?.response?.code || "-"}
                    </span>
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-xs text-slate-500 uppercase">
                    {t("Sender (Payer) Endpoint")}
                  </dt>
                  <dd className="font-mono text-xs text-slate-600 bg-slate-50 p-1 rounded mt-1 truncate">
                    {outerMessageHeader?.source?.endpoint || "-"}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* 3. DETAILED LINE ITEMS */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="p-2 border-b border-slate-200 flex justify-between items-center bg-gray-100">
              <h3 className="font-bold text-sm uppercase text-slate-700">
                {t("Detailed Line Items")}
              </h3>
              <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-xs font-bold">
                {lineItems?.length || 0} {t("Items")}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-300 text-xs text-gray-900 uppercase font-semibold border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-2">{t("Payer Response ID")}</th>
                    <th className="px-4 py-2">{t("Dates")}</th>
                    <th className="px-4 py-2">{t("Claim Request ID")}</th>
                    <th className="px-4 py-2 text-right">{t("Breakdown")}</th>
                    <th className="px-4 py-2 text-right">{t("Net Amount")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {lineItems && lineItems.length > 0 ? (
                    lineItems.map((item, idx) => (
                      <tr
                        key={idx}
                        className="hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-6 py-4 align-top">
                          <div
                            onClick={() =>
                              navigate("/claims/details", {
                                state: {
                                  claimRef: item.request?.identifier?.value,
                                  fromPayment: true,
                                },
                              })
                            }
                            className="font-mono text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded inline-block cursor-pointer hover:bg-blue-100 border border-blue-100"
                          >
                            {item.request?.identifier?.value || "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top">
                          <div className="font-mono text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded inline-block border border-slate-200">
                            {item.response?.identifier?.value || "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top text-slate-600">
                          {formatDate(item.date)}
                        </td>
                        <td className="px-6 py-4 align-top text-right text-xs text-slate-500">
                          <div className="flex flex-col gap-1 items-end">
                            <div className="flex justify-between w-32">
                              <span>{t("Base:")}</span>
                              <span className="font-medium text-slate-700">
                                {formatCurrency(
                                  item.breakdown?.payment?.value,
                                  item.breakdown?.payment?.currency,
                                )}
                              </span>
                            </div>
                            {item.breakdown?.earlyFee?.value > 0 && (
                              <div className="flex justify-between w-32 text-orange-600">
                                <span>{t("Early Fee:")}</span>
                                <span>
                                  -
                                  {formatCurrency(
                                    item.breakdown.earlyFee?.value,
                                  )}
                                </span>
                              </div>
                            )}
                            {item.breakdown?.nphiesFee?.value > 0 && (
                              <div className="flex justify-between w-32 text-orange-600">
                                <span>NPHIES:</span>
                                <span>
                                  -
                                  {formatCurrency(
                                    item.breakdown.nphiesFee?.value,
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top text-right font-bold text-emerald-600 text-base">
                          {formatCurrency(
                            item.amount?.value,
                            item.amount?.currency,
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="5"
                        className="p-6 text-center text-slate-400"
                      >
                        {t("No line items found.")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>

        {/* === RIGHT SIDEBAR === */}
        <aside className="space-y-2">
          {/* 1. WORKFLOW STATUS */}
          <div className="bg-white rounded-lg  border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-300 bg-gray-100 flex items-center gap-2">
              <FaExchangeAlt className="text-[#0070C2]" />
              <h3 className="font-bold text-xs uppercase text-slate-700">
                {t("Workflow State")}
              </h3>
            </div>
            <div className="px-4 py-3 relative">
              {/* Timeline Line */}
              <div
                className={`absolute ${i18n.dir() === "rtl" ? "right-7" : "left-7"} top-6 bottom-6 w-0.5 bg-slate-200`}
              ></div>

              {/* Item 1 */}
              <div className="relative pl-10 mb-6">
                <div
                  className={`absolute ${i18n.dir() === "rtl" ? "right-3" : "left-3"} top-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white ring-1 ring-emerald-500`}
                ></div>
                <span className="block text-xs uppercase text-slate-400 font-bold">
                  {t("Current")}
                </span>
                <span className="inline-block px-2 py-0.5 mt-1 text-xs font-bold text-white bg-emerald-600 rounded">
                  {t("Poll Response Received")}
                </span>
                <div className="text-xs text-slate-500 mt-1">
                  {formatDateTime(rawPaymentData.payment_note_date)}
                </div>
              </div>

              {/* Row for Status and Note Date */}
              <div className="flex items-start">
                {/* Item 2 */}
                <div className="relative pl-10 mb-6 flex-1">
                  <div
                    className={`absolute ${i18n.dir() === "rtl" ? "right-3" : "left-3"} top-0 w-3 h-3 rounded-full bg-slate-300 border-2 border-white ring-1 ring-slate-300`}
                  ></div>
                  <span className="block text-xs uppercase text-slate-400 font-bold">
                    {t("Status")}
                  </span>
                  <div className="text-sm font-medium text-slate-800 capitalize">
                    {rawPaymentData?.payment_status || t("Pending")}
                  </div>
                </div>

                {/* Item 3 */}
                <div className="relative pl-10 flex-1">
                  <div
                    className={`absolute ${i18n.dir() === "rtl" ? "right-3" : "left-3"} top-0 w-3 h-3 rounded-full bg-slate-300 border-2 border-white ring-1 ring-slate-300`}
                  ></div>
                  <span className="block text-xs uppercase text-slate-400 font-bold">
                    {t("Note Date")}
                  </span>
                  <div className="text-sm font-medium text-slate-800">
                    {formatDate(rawPaymentData.payment_note_date)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 2. ORGANIZATIONS */}
          <div className="bg-white rounded-lg  border border-slate-200">
            <div className="px-4 py-2 border-b border-gray-300 bg-gray-100 flex items-center gap-2">
              <FaBuilding className="text-[#0070C2]" />
              <h3 className="font-bold text-xs uppercase text-slate-700">
                {t("Participating Orgs")}
              </h3>
            </div>
            <div className="px-4 py-3 space-y-3">
              {/* Payer */}
              <div
                className={`relative ${i18n.dir() === "rtl" ? "pr-4 border-r-2" : "pl-4 border-l-2"} border-blue-500`}
              >
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  {t("Payer (Insurer)")}
                </span>
                <div className="text-sm font-semibold text-slate-900 leading-tight mt-1 mb-2">
                  {insurerOrg?.name ||
                    rawPaymentData.payer_name_en ||
                    "Unknown Insurer"}
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">{t("License:")}</span>
                    <span className="font-mono bg-slate-100 px-1 rounded text-slate-700">
                      {insurerOrg?.identifier?.[0]?.value ||
                        rawPaymentData.payer_id ||
                        "-"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">{t("Type:")}</span>
                    <span className="text-slate-700 capitalize">
                      {insurerOrg?.type?.[0]?.coding?.[0]?.display ||
                        "Insurance Company"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payee */}
              <div
                className={`relative ${i18n.dir() === "rtl" ? "pr-4 border-r-2" : "pl-4 border-l-2"} border-emerald-500`}
              >
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  {t("Payee (Provider)")}
                </span>
                <div className="text-sm font-semibold text-slate-900 leading-tight mt-1 mb-2">
                  {providerOrg?.name ||
                    rawPaymentData.providers_name_en ||
                    "Unknown Provider"}
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">License:</span>
                    <span className="font-mono bg-slate-100 px-1 rounded text-slate-700">
                      {providerOrg?.identifier?.[0]?.value || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">{t("Type:")}</span>
                    <span className="text-slate-700 capitalize">
                      {providerOrg?.type?.[0]?.coding?.[0]?.display ||
                        "Healthcare Provider"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 3. TECHNICAL META */}
          {/* <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-300 bg-gray-100 flex items-center gap-2">
              <FaNetworkWired className="text-[#0070C2]" />
              <h3 className="font-bold text-xs uppercase text-slate-700">
                {t("Technical Meta")}
              </h3>
            </div>
            <div className="px-4 py-3 space-y-3">
              <div className="group">
                <span className="block text-[10px] uppercase text-slate-400 font-bold mb-0.5">
                  {t("Payment Reconciliation ID")}
                </span>
                <div className="font-mono text-xs text-slate-600 bg-slate-100 p-1.5 rounded border border-slate-200 break-all select-all">
                  {reconciliation?.id || "-"}
                </div>
              </div>
              <div className="group">
                <span className="block text-[10px] uppercase text-slate-400 font-bold mb-0.5">
                  {t("Task ID")}
                </span>
                <div className="font-mono text-xs text-slate-600 bg-slate-100 p-1.5 rounded border border-slate-200 break-all select-all">
                  {task?.id || "-"}
                </div>
              </div>
              <div className="group">
                <span className="block text-[10px] uppercase text-slate-400 font-bold mb-0.5">
                  {t("Message Header ID")}
                </span>
                <div className="font-mono text-xs text-slate-600 bg-slate-100 p-1.5 rounded border border-slate-200 break-all select-all">
                  {outerMessageHeader?.id || "-"}
                </div>
              </div>
            </div>
          </div> */}
        </aside>
      </div>
    </div>
  );
}

export default PaymentDetails;
